/**
 * AANKH - Camera Panel Component
 * 
 * @author Harshit Khemani
 * @version 1.9.0
 * @description Enhanced camera panel with auto picture, settings configuration, and reset to default
 */

import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDevice, CameraStatus } from '../hooks/useDevice';

function CameraPanel() {
  const {
    hasCamera,
    cameraStatus,
    cameraImage,
    cameraProgress,
    cameraConfiguration,
    cameraConfigurationRanges,
    autoPicture,
    setAutoPicture,
    takePicture,
    focusCamera,
    updateCameraSettings,
    resetCameraConfiguration,
  } = useDevice();

  const [gallery, setGallery] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showFlash, setShowFlash] = useState(false);
  const [captureQueue, setCaptureQueue] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [localConfig, setLocalConfig] = useState({});

  // Initialize local config from camera configuration
  useEffect(() => {
    if (cameraConfiguration) {
      setLocalConfig(cameraConfiguration);
    }
  }, [cameraConfiguration]);

  // Handle photo capture - fire and forget for rapid captures
  const handleCapture = useCallback(() => {
    if (!hasCamera) return;
    
    // Immediate visual feedback
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 100);
    
    // Queue the capture (don't wait for completion)
    setCaptureQueue(prev => prev + 1);
    
    // Fire and forget - don't await
    takePicture().catch(error => {
      console.error('[AANKH] Capture failed:', error);
    });
  }, [hasCamera, takePicture]);

  // Add to gallery
  useEffect(() => {
    if (cameraImage && !gallery.includes(cameraImage)) {
      setGallery(prev => [cameraImage, ...prev].slice(0, 12));
      setSelectedImage(cameraImage);
    }
  }, [cameraImage, gallery]);

  // Only show capturing status, but don't block UI
  const isCapturing = cameraStatus === CameraStatus.TAKING_PICTURE || 
                      cameraStatus === CameraStatus.FOCUSING;
  const displayImage = selectedImage || cameraImage;
  
  // Reset capture queue when image arrives
  useEffect(() => {
    if (cameraImage && captureQueue > 0) {
      setCaptureQueue(prev => Math.max(0, prev - 1));
    }
  }, [cameraImage, captureQueue]);

  // Handle config change
  const handleConfigChange = useCallback((key, value) => {
    const newConfig = { ...localConfig, [key]: Number(value) };
    setLocalConfig(newConfig);
    updateCameraSettings({ [key]: Number(value) });
  }, [localConfig, updateCameraSettings]);

  // Handle reset
  const handleReset = useCallback(async () => {
    await resetCameraConfiguration();
    // Local config will update via useEffect when cameraConfiguration changes
  }, [resetCameraConfiguration]);

  return (
    <motion.div 
      className="glass-panel rounded-3xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 border-b border-white/40 backdrop-blur-sm"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset'
        }}
      >
        <motion.div 
          className="flex items-center justify-between gap-3 sm:gap-4"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
            <motion.div 
              className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br from-primary/20 to-pink-100/80 
                flex items-center justify-center text-xl sm:text-2xl lg:text-3xl backdrop-blur-sm border border-white/60 flex-shrink-0"
              style={{
                boxShadow: '0 2px 8px rgba(255,88,147,0.15), 0 1px 0 rgba(255,255,255,0.6) inset'
              }}
              whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              📷
            </motion.div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-900 text-base sm:text-lg lg:text-xl truncate">Camera</h3>
              <p className="text-xs sm:text-sm lg:text-base text-gray-500 truncate">Capture photos from your glasses</p>
            </div>
          </div>
          
          {/* Auto Picture Toggle */}
          <motion.label
            className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg glass-button cursor-pointer flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <input
              type="checkbox"
              checked={autoPicture}
              onChange={(e) => setAutoPicture(e.target.checked)}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded text-primary focus:ring-primary focus:ring-2"
              disabled={!hasCamera}
            />
            <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Auto</span>
          </motion.label>
        </motion.div>
      </div>

      {/* Preview */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100/80 via-gray-50/60 to-gray-100/80 backdrop-blur-sm overflow-hidden">
        {displayImage ? (
          <motion.img
            key={displayImage}
            src={displayImage}
            alt="Capture"
            className="w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          />
        ) : (
          <motion.div 
            className="w-full h-full flex flex-col items-center justify-center text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.svg 
              className="w-12 h-12 sm:w-16 sm:h-16 mb-2 sm:mb-3 opacity-40" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              animate={{ 
                scale: [1, 1.05, 1],
                opacity: [0.4, 0.5, 0.4]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </motion.svg>
            <p className="text-xs sm:text-sm font-medium">No image yet</p>
          </motion.div>
        )}

        {/* Flash overlay */}
        <AnimatePresence>
          {showFlash && (
            <motion.div
              className="absolute inset-0 bg-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            />
          )}
        </AnimatePresence>

        {/* Capture indicator */}
        <AnimatePresence>
          {(isCapturing || captureQueue > 0) && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="absolute top-3 sm:top-4 left-3 sm:left-4 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full 
                bg-black/50 backdrop-blur-md text-white text-xs font-semibold
                flex items-center gap-2 sm:gap-2.5 border border-white/10"
              style={{
                boxShadow: '0 4px 16px rgba(0,0,0,0.2), 0 1px 0 rgba(255,255,255,0.1) inset'
              }}
            >
              <motion.div
                className="relative w-2 h-2 sm:w-2.5 sm:h-2.5"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.5, 1]
                }}
                transition={{ duration: 0.8, repeat: Infinity }}
              >
                <div className="absolute inset-0 rounded-full bg-primary" />
                <div className="absolute inset-0 rounded-full bg-primary blur-md opacity-60" />
              </motion.div>
              {captureQueue > 1 ? `${captureQueue} queued` : cameraStatus === CameraStatus.FOCUSING ? 'Focusing' : 'Capturing'}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress bar */}
        {isCapturing && cameraProgress > 0 && cameraProgress < 1 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${cameraProgress * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-4 sm:p-6 lg:p-8"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 100%)'
        }}
      >
        <div className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6">
          {/* Focus button */}
          <motion.button
            onClick={focusCamera}
            disabled={!hasCamera}
            className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full glass-button text-gray-700
              disabled:opacity-40 disabled:cursor-not-allowed
              flex items-center justify-center min-w-[56px] min-h-[56px]"
            whileHover={!hasCamera ? {} : { scale: 1.1, rotate: 15 }}
            whileTap={!hasCamera ? {} : { scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            aria-label="Focus camera"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" 
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </motion.button>

          {/* Shutter button */}
          <motion.button
            onClick={handleCapture}
            disabled={!hasCamera}
            className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-full border-[3px] sm:border-[4px]
              flex items-center justify-center min-w-[80px] min-h-[80px]
              border-primary/80 bg-white/60 backdrop-blur-md disabled:border-gray-200/60 disabled:bg-white/40"
            style={{
              boxShadow: !hasCamera ? {} : '0 4px 16px rgba(255,88,147,0.2), 0 1px 0 rgba(255,255,255,0.8) inset'
            }}
            whileHover={!hasCamera ? {} : { scale: 1.08 }}
            whileTap={!hasCamera ? {} : { scale: 0.92 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <motion.div
              className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${
                !hasCamera ? 'bg-gray-200' : 'bg-gradient-to-br from-primary to-pink-500'
              }`}
              style={!hasCamera ? {} : {
                boxShadow: '0 2px 8px rgba(255,88,147,0.4), 0 1px 0 rgba(255,255,255,0.3) inset'
              }}
              animate={showFlash ? { 
                scale: [1, 0.85, 1],
              } : {}}
              transition={{ duration: 0.1 }}
            >
              {!hasCamera ? null : (
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 to-transparent" />
              )}
            </motion.div>
          </motion.button>

          {/* Download button */}
          <motion.a
            href={displayImage || '#'}
            download={displayImage ? `aankh-${Date.now()}.jpg` : undefined}
            className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full glass-button text-gray-700
              flex items-center justify-center min-w-[56px] min-h-[56px]
              ${!displayImage ? 'opacity-40 pointer-events-none' : ''}`}
            whileHover={!displayImage ? {} : { scale: 1.1, y: -2 }}
            whileTap={!displayImage ? {} : { scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            aria-label="Download image"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" 
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </motion.a>
        </div>

        {/* Settings Toggle */}
        {cameraConfiguration && cameraConfigurationRanges && (
          <motion.button
            onClick={() => setShowSettings(!showSettings)}
            className="w-full py-2.5 sm:py-3 rounded-xl glass-button text-gray-700 font-medium text-sm sm:text-base mb-4 sm:mb-6"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Camera Settings</span>
              <motion.svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                animate={{ rotate: showSettings ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </div>
          </motion.button>
        )}

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && cameraConfiguration && cameraConfigurationRanges && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/40 space-y-4 sm:space-y-5">
                {Object.keys(cameraConfigurationRanges).map((key) => {
                  const range = cameraConfigurationRanges[key];
                  const value = localConfig[key] ?? range.min;
                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm sm:text-base font-medium text-gray-700 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        <span className="text-xs sm:text-sm text-gray-600 font-mono">{value}</span>
                      </div>
                      <input
                        type="range"
                        min={range.min}
                        max={range.max}
                        value={value}
                        onChange={(e) => handleConfigChange(key, e.target.value)}
                        disabled={!hasCamera || cameraStatus !== CameraStatus.IDLE}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background: `linear-gradient(to right, #FF5893 0%, #FF5893 ${((value - range.min) / (range.max - range.min)) * 100}%, #e5e7eb ${((value - range.min) / (range.max - range.min)) * 100}%, #e5e7eb 100%)`
                        }}
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{range.min}</span>
                        <span>{range.max}</span>
                      </div>
                    </div>
                  );
                })}
                <motion.button
                  onClick={handleReset}
                  disabled={!hasCamera || cameraStatus !== CameraStatus.IDLE}
                  className="w-full py-2.5 sm:py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Reset to Default
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gallery */}
        {gallery.length > 0 && (
          <motion.div 
            className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/40"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs sm:text-sm lg:text-base font-semibold text-gray-600 mb-3 sm:mb-4 px-1">Recent Captures</p>
            <div className="flex gap-2 sm:gap-3 lg:gap-4 overflow-x-auto scrollbar-hide pb-2">
              {gallery.map((img, i) => (
                <motion.button
                  key={i}
                  onClick={() => setSelectedImage(img)}
                  className={`relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl overflow-hidden flex-shrink-0
                    border-2 backdrop-blur-sm min-w-[64px] min-h-[64px]
                    ${selectedImage === img
                      ? 'border-primary/80 bg-white/40 ring-2 ring-primary/30 shadow-lg shadow-primary/20'
                      : 'border-white/40 bg-white/20 hover:border-white/60 hover:bg-white/30'}`}
                  style={{
                    boxShadow: selectedImage === img 
                      ? '0 4px 12px rgba(255,88,147,0.2), 0 1px 0 rgba(255,255,255,0.6) inset'
                      : '0 2px 4px rgba(0,0,0,0.05), 0 1px 0 rgba(255,255,255,0.4) inset'
                  }}
                  whileHover={{ scale: 1.08, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default CameraPanel;
